import { previewKindOfContentType as thumbnailKindOf, startsWith } from './materials';

/**
 * A material's thumbnail: the small JPEG a grid card shows. Pure — the Worker passes the
 * declared length and the bytes in, and the browser passes the listing in.
 *
 * The browser draws it and the Worker only checks it, which is why the rules here are narrow:
 *
 * - **JPEG only, by magic bytes.** The browser always encodes with `canvas.toBlob('image/jpeg')`,
 *   so anything else is not a thumbnail this app made. A JPEG cannot execute; a PNG cannot
 *   either, but one type is one less thing to reason about on the way back out, and an SVG or
 *   an HTML body labelled `image/jpeg` is refused here before it is stored.
 * - **At most `MAX_THUMBNAIL_BYTES`, by the declared length before the body is read, and again
 *   by the bytes.** The same two bounds the upload holds, for the same reason: a chunked body
 *   has no length, and a truthful-looking header can still be followed by a longer stream.
 * - **The page count is a display number.** The browser counted it; nothing is decided by it.
 *   Anything other than a plain integer from 1 to `MAX_PAGE_COUNT` is dropped, not refused, so
 *   a bad count never costs the thumbnail.
 */

/** 256 KB. A 320 px wide JPEG at quality 0.8 is usually 15–60 KB. */
export const MAX_THUMBNAIL_BYTES = 256 * 1024;

/** The width the browser draws at. Cards are 180–320 px wide. */
export const THUMBNAIL_WIDTH = 320;

/** A page taller than twice its width is drawn to this height instead, so a scroll stays small. */
export const THUMBNAIL_MAX_HEIGHT = 640;

/** The first quality tried, and the one retry when the first is over the cap. */
export const THUMBNAIL_QUALITY = 0.8;
export const THUMBNAIL_RETRY_QUALITY = 0.5;

export const MAX_PAGE_COUNT = 10_000;

/** How many missing thumbnails one visit to a room will fill in the background. */
export const BACKFILL_PER_VISIT = 5;

export type ThumbnailRefusal = 'length_required' | 'too_large' | 'not_jpeg' | 'empty';

export type ThumbnailDecision = { ok: true } | { ok: false; reason: ThumbnailRefusal };

/** Before a byte is read: a length, and one under the cap. */
export function decideThumbnailLength(declared: number | null): ThumbnailDecision {
  if (declared === null) return { ok: false, reason: 'length_required' };
  if (declared > MAX_THUMBNAIL_BYTES) return { ok: false, reason: 'too_large' };
  return { ok: true };
}

const JPEG_MAGIC = [0xff, 0xd8, 0xff] as const;

/** After the body is read: the real size, and the JPEG signature. */
export function decideThumbnailBytes(bytes: Uint8Array): ThumbnailDecision {
  if (bytes.byteLength === 0) return { ok: false, reason: 'empty' };
  if (bytes.byteLength > MAX_THUMBNAIL_BYTES) return { ok: false, reason: 'too_large' };
  if (!startsWith(bytes, JPEG_MAGIC)) return { ok: false, reason: 'not_jpeg' };
  return { ok: true };
}

/** A plain decimal integer from 1 to `MAX_PAGE_COUNT`, or null. */
export function parsePageCount(raw: string | null): number | null {
  if (raw === null || !/^[0-9]{1,5}$/.test(raw)) return null;
  const value = Number(raw);
  return value >= 1 && value <= MAX_PAGE_COUNT ? value : null;
}

/**
 * Which materials get a drawn thumbnail: the three the preview route serves inline, and
 * nothing else. Word, PowerPoint and text keep their icon until Office conversion exists.
 */
export { thumbnailKindOf };

export interface BackfillRow {
  readonly id: string;
  readonly contentType: string;
  readonly uploadedBy: string;
  readonly hasThumbnail?: boolean;
}

/**
 * Which listed materials the room should draw a thumbnail for in the background: the
 * viewer's own (only the uploader may store one), drawable, still without one, not already
 * tried this visit — in listing order, at most `limit`.
 */
export function backfillCandidates<T extends BackfillRow>(
  materials: readonly T[],
  viewerUserId: string | null,
  tried: ReadonlySet<string>,
  limit: number,
): T[] {
  if (viewerUserId === null || limit <= 0) return [];
  return materials
    .filter((material) => material.uploadedBy === viewerUserId
      && material.hasThumbnail !== true
      && !tried.has(material.id)
      && thumbnailKindOf(material.contentType) !== null)
    .slice(0, limit);
}
